import classNames from 'classnames'
import Link from 'next/link'

import Icon from '@/components/Icon/Icon'
import InteractiveTooltip from '@/components/InteractiveTooltip/InteractiveTooltip'
import Mana from '@/components/Mana/Mana'
import { CardLabelVariantType, GenericCardType, PageCardType } from '@/types/card'

import styles from './CardLabel.module.scss'

const LINK_FAQ_SYNERGY = '/faq?q=1'
const LINK_ARTICLE_LIFT = '/articles/from-synergy-to-lift-the-math-behind-edhrecs-new-era'

const formatSigFig = (num: number) => {
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(2).replace(/\.0+$/, '') + 'M'

  if (num >= 100_000) return Math.round(num / 1_000) + 'K'

  if (num >= 10_000) return (num / 1_000).toFixed(1).replace(/\.0+$/, '') + 'K'

  if (num >= 1_000) return (num / 1_000).toFixed(2).replace(/\.0+$/, '') + 'K'

  return num.toString()
}

export const formatLift = (lift: number) => (lift > 99 ? '99+' : lift.toFixed(Math.round(lift) < 10 ? 2 : 1))

const formatSynergy = (synergy: number) => `${(synergy * 100).toFixed(0)}%`

const formatInclusion = (numDecks: number, potentialDecks: number, numeralsInput: number = 2) => {
  if (potentialDecks <= 0) return null
  const numerals = numeralsInput >= 2 && numeralsInput <= 5 ? numeralsInput : 2
  const inclusion = (numDecks / potentialDecks) * 100
  const inclusionRounded = Math.round(inclusion)
  let decimals = inclusionRounded >= 100 ? numerals - 3 : inclusionRounded >= 10 ? numerals - 2 : numerals - 1
  decimals = decimals > 0 ? decimals : 0

  return `${inclusion.toFixed(decimals)}%`
}

const formatUnpopularity = (unpopularity: number) => `${(unpopularity * 100).toFixed(1)}%`

const CardLabelSalt = ({ card }: { card?: GenericCardType }) => {
  // TODO: replace with real thing
  const salt = card?.label?.split(' ')?.[2]?.split('\n')?.[0]

  if (!salt) return null

  return (
    <div className={styles.salt}>
      <span>Salt Score: {salt}</span>
    </div>
  )
}

const CardLabelTotal = ({ card, prefix, suffix }: { card?: GenericCardType; prefix?: string; suffix?: string }) => {
  const numDecks = card?.num_decks

  if (numDecks === undefined) return null

  return (
    <div className={styles.line}>
      {prefix && <span>{prefix}</span>}
      <span>{numDecks.toLocaleString()}</span>
      {suffix && <span>{suffix}</span>}
      <span>decks</span>
    </div>
  )
}

const CardLabelInclusion = ({
  card,
  numerals,
  placement = 'bottom',
}: {
  card?: GenericCardType
  numerals?: 2 | 3
  placement?: 'top' | 'bottom'
}) => {
  const numDecks = card?.num_decks
  const potentialDecks = card?.potential_decks

  if (numDecks === undefined || potentialDecks === undefined) return null

  const inclusion = formatInclusion(numDecks, potentialDecks, numerals)

  const numDecksFormatted = formatSigFig(numDecks)
  const potentialDecksFormatted = formatSigFig(potentialDecks)

  return potentialDecks > 0 ? (
    <InteractiveTooltip
      placement={placement}
      tooltipChildren={
        <div className={styles.line}>
          <span className={classNames(styles.fraction)}>
            <span>
              <strong>{numDecks.toLocaleString()}</strong> decks
            </span>
            <span className={styles.denominator}>
              <strong>{potentialDecks.toLocaleString()}</strong> potential decks
            </span>
          </span>
        </div>
      }
    >
      <div className={styles.line}>
        <span className={styles.stat}>{inclusion}</span>
        <span className={styles.label}>inclusion</span>
        <span>
          <span className={classNames(styles.fraction, 'ms-2')}>
            <span className={styles.numerator}>
              <b>{numDecksFormatted}</b> decks
            </span>
            <span className={styles.denominator}>
              <b>{potentialDecksFormatted}</b> decks
            </span>
          </span>
        </span>
      </div>
    </InteractiveTooltip>
  ) : (
    <div className={styles.line}>
      <span className={styles.stat}>0</span>
      <span className={styles.label}>decks</span>
    </div>
  )
}

const CardLabelRank = ({ card }: { card?: GenericCardType }) => {
  const rank = card?.rank?.toLocaleString()
  if (rank === undefined) return null

  return (
    <div className={styles.line}>
      <span className={styles.rank}>Rank #{rank}</span>
    </div>
  )
}

const CardLabelLift = ({ card, pageCommander }: { card?: GenericCardType; pageCommander?: PageCardType }) => {
  const lift = card?.lift

  if (lift !== undefined) {
    const liftFormatted = formatLift(lift)

    // Formatting rounds & truncates
    // The formatted number is used to determine the icon
    const liftFormattedFloat = parseFloat(liftFormatted)
    const icon =
      liftFormattedFloat >= 10
        ? 'anglesUp'
        : liftFormattedFloat >= 3
          ? 'angleUp'
          : liftFormattedFloat >= 0.9
            ? false
            : liftFormattedFloat >= 0.3
              ? 'angleDown'
              : 'anglesDown'

    return (
      <InteractiveTooltip
        placement='bottom'
        tooltipChildren={
          <Link className={styles.line} href={LINK_ARTICLE_LIFT} target='_blank'>
            <span className={styles.tooltipContent}>
              <strong>Lift</strong> measures the strength of the association between two cards, relative to what would
              be expected if they were independent.
            </span>
            <span className={styles.iconRight}>
              <Icon icon='circleQuestion' />
            </span>
          </Link>
        }
      >
        <Link className={styles.line} href={LINK_ARTICLE_LIFT} target='_blank'>
          {icon && (
            <span className={styles.iconLeft}>
              <Icon icon={icon} />
            </span>
          )}
          <span className={styles.statSmall}>{liftFormatted}</span>
          <span className={classNames(styles.label, styles.underline)}>lift</span>
        </Link>
      </InteractiveTooltip>
    )
  }

  // Fallback to Synergy if Lift is unavailable
  const synergy = card?.synergy
  const numDecks = card?.num_decks
  const potentialDecks = card?.potential_decks

  if (synergy === undefined || numDecks === undefined || potentialDecks === undefined) return null

  const synergyFormatted = formatSynergy(synergy)
  let arrowsUp = 0
  if (synergy > 0.6) arrowsUp = 2
  else if (synergy > 0.2) arrowsUp = 1

  const arrows = Array(arrowsUp).fill(<Icon icon='arrowUp' />)

  let content = (
    <Link className={styles.line} href={LINK_FAQ_SYNERGY} target='_blank'>
      {arrows && <span className={styles.iconLeft}>{arrows}</span>}
      <span className={styles.statSmall}>{synergyFormatted}</span>
      <span className={classNames(styles.label, styles.underline)}>synergy</span>
    </Link>
  )

  if (pageCommander !== undefined) {
    content = (
      <InteractiveTooltip
        placement='bottom'
        tooltipChildren={
          <Link className={styles.synergyTooltipBody} href={LINK_FAQ_SYNERGY} target='_blank'>
            <span>synergy = </span>
            <span>
              <div>
                usage in {pageCommander.name.split(' // ')[0]} ({Math.round((100 * numDecks) / potentialDecks)}
                %)
              </div>
              <div>
                - usage in <Mana colors={pageCommander.color_identity} /> (
                {Math.round(Math.round((100 * numDecks) / potentialDecks) - 100 * synergy)}
                %)
              </div>
            </span>
          </Link>
        }
      >
        {content}
      </InteractiveTooltip>
    )
  }

  return content
}

const CardLabelAddedTo = ({ card }: { card?: GenericCardType }) => {
  if (card?.num_decks === undefined || card?.potential_decks === undefined) return null

  return (
    <div className={styles.line}>
      Added to <b>{formatInclusion(card.num_decks, card.potential_decks, 3)}</b> of decks
    </div>
  )
}

const CardLabelCutFrom = ({ card }: { card?: GenericCardType }) => {
  if (card?.unpopularity === undefined) return null

  return (
    <div className={styles.line}>
      Cut from <b>{formatUnpopularity(card.unpopularity)}</b> of decks
    </div>
  )
}

type Props = {
  card?: GenericCardType
  labelVariant?: CardLabelVariantType
  pageCommander?: PageCardType
}
const getLabelContent = (card?: GenericCardType, labelVariant?: CardLabelVariantType, pageCommander?: PageCardType) => {
  switch (labelVariant) {
    case 'directory-count':
      return (
        <>
          <CardLabelTotal card={card} />
        </>
      )
    case 'directory-inclusion':
      return (
        <>
          <CardLabelInclusion card={card} />
        </>
      )
    case 'directory-rank':
      return (
        <>
          <CardLabelRank card={card} />
          <CardLabelTotal card={card} />
        </>
      )
    case 'directory-tags':
      return (
        <>
          <CardLabelTotal card={card} suffix={card?.name} />
        </>
      )
    case 'featured':
      return pageCommander?.is_commander ? (
        <>
          <CardLabelRank card={card} />
          <CardLabelTotal card={card} />
        </>
      ) : (
        <>
          <CardLabelInclusion card={card} numerals={3} />
        </>
      )
    case 'name':
      return <div className={styles.defaultLabel}>{card?.name}</div>
    case 'precon-add':
      return <CardLabelAddedTo card={card} />
    case 'precon-cut':
      return <CardLabelCutFrom card={card} />
    case 'rec':
      return card?.num_decks !== undefined && card?.potential_decks !== undefined ? (
        <>
          <CardLabelInclusion card={card} placement='top' />
          <CardLabelLift card={card} pageCommander={pageCommander} />
        </>
      ) : (
        <CardLabelTotal card={card} />
      )
    case 'salt':
      return (
        <>
          <CardLabelSalt card={card} />
          <CardLabelTotal card={card} />
        </>
      )
    default:
      return card?.label ? <div className={styles.defaultLabel}>{card.label}</div> : null
  }
}

const CardLabel = ({ card, labelVariant, pageCommander }: Props) => {
  return <div className={styles.container}>{getLabelContent(card, labelVariant, pageCommander)}</div>
}

export default CardLabel
